import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api";

/**
 * CourseSettingsPanel
 * Pick a catalogue category, and after release tweak Active and the New ribbon.
 *
 * Reads and writes through the /api/admin/courses routes rather than /coursesData,
 * because that endpoint is cached at the edge for 300s and in the client for 5 minutes,
 * which would make a save look like it had not taken effect.
 *
 * Props:
 *  isOpen           boolean
 *  onClose          () => void
 *  selectedCourse   course object from the course selector
 *  onCourseUpdated  (course) => void — called after a successful save
 */

const COMING_SOON = "Coming Soon";
const NEW_CATEGORY = "__new__";

const collectCatalogueCategories = (courses) => {
  const names = new Set();
  (courses || []).forEach((item) => {
    const name = (item.courseCategory || "").trim();
    if (name && name !== COMING_SOON) names.add(name);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
};

const EMPTY_FORM = {
  courseCategory: "",
  courseIsActive: false,
  courseIsNew: false,
};

const settingsPayload = (form) => ({
  courseCategory: form.courseCategory,
  courseIsActive: form.courseIsActive,
  courseIsNew: form.courseIsNew,
});

const errorMessage = (err) =>
  err?.response?.data?.error || err?.message || "Something went wrong";

const CourseSettingsPanel = ({ isOpen, onClose, selectedCourse, onCourseUpdated }) => {
  const [course, setCourse] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [catalogueCategories, setCatalogueCategories] = useState([]);
  const [categoryMode, setCategoryMode] = useState("existing");
  const [newCategoryName, setNewCategoryName] = useState("");

  const loadCourse = useCallback(async () => {
    if (!selectedCourse) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/api/admin/courses");
      const match =
        response.data.find((item) => item._id === selectedCourse._id) ||
        response.data.find((item) => item.courseTitle === selectedCourse.courseTitle);

      if (!match) {
        setError("Could not find this course in the database.");
        return;
      }

      setCourse(match);
      setCatalogueCategories(collectCatalogueCategories(response.data));
      setCategoryMode("existing");
      setNewCategoryName("");
      setForm({
        courseCategory: match.courseCategory || "",
        courseIsActive: Boolean(match.courseIsActive),
        courseIsNew: Boolean(match.courseIsNew),
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (isOpen) loadCourse();
  }, [isOpen, loadCourse]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const categoryOptions = useMemo(() => {
    const names = new Set(catalogueCategories);
    if (form.courseCategory && form.courseCategory !== COMING_SOON) {
      names.add(form.courseCategory);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [catalogueCategories, form.courseCategory]);

  const resolvedCategory = () => {
    if (categoryMode === "new") return newCategoryName.trim();
    return (form.courseCategory || "").trim();
  };

  const isUnreleased =
    course &&
    !course.courseIsActive &&
    (!course.courseCategory || course.courseCategory === COMING_SOON);

  const showReleaseFlags =
    Boolean(course?.courseCategory) && course.courseCategory !== COMING_SOON;

  const persistCourse = async (nextForm, successNotice) => {
    const response = await api.put(`/api/admin/courses/${course._id}`, settingsPayload(nextForm));
    setCourse(response.data);
    setForm({
      courseCategory: response.data.courseCategory || nextForm.courseCategory,
      courseIsActive: Boolean(response.data.courseIsActive),
      courseIsNew: Boolean(response.data.courseIsNew),
    });
    if (response.data.courseCategory && response.data.courseCategory !== COMING_SOON) {
      setCatalogueCategories((prev) => {
        const names = new Set(prev);
        names.add(response.data.courseCategory);
        return Array.from(names).sort((a, b) => a.localeCompare(b));
      });
      setCategoryMode("existing");
      setNewCategoryName("");
    }
    setNotice(successNotice);
    if (onCourseUpdated) onCourseUpdated(response.data);
    setTimeout(() => setNotice(null), 3000);
  };

  const handleSave = async () => {
    if (!course) return;
    const category = resolvedCategory();
    if (!category) {
      setError("Choose an existing category or type a new one.");
      return;
    }

    if (isUnreleased) {
      if (category === COMING_SOON) {
        setError("Pick an existing category or type a new one.");
        return;
      }
      const confirmed = window.confirm(
        `Release this course in “${category}”? It will be Active and show the New ribbon.`
      );
      if (!confirmed) return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (isUnreleased) {
        await persistCourse(
          { courseCategory: category, courseIsActive: true, courseIsNew: true },
          "Course released."
        );
      } else {
        await persistCourse({ ...form, courseCategory: category }, "Saved.");
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className={`course-overview-backdrop ${isOpen ? "open" : ""}`}
        aria-hidden="true"
        onClick={onClose}
      />

      <div
        className={`course-overview-drawer course-settings-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-label="Course Settings"
      >
        <div className="course-overview-header">
          <h2 className="course-overview-title">
            {selectedCourse ? selectedCourse.courseTitle : "Course Settings"}
          </h2>
          <button
            className="course-overview-close"
            onClick={onClose}
            aria-label="Close course settings"
          >
            ✕
          </button>
        </div>

        <div className="course-overview-body">
          {loading && <div className="course-overview-loading">Loading course…</div>}
          {error && <div className="course-overview-error">{error}</div>}
          {notice && <div className="course-settings-notice">{notice}</div>}

          {!loading && course && (
            <div className="course-settings-section">
              <div className="course-settings-section-title">Category</div>

              <div className="course-settings-label">Category</div>
              <select
                className="course-settings-input"
                value={categoryMode === "new" ? NEW_CATEGORY : form.courseCategory || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === NEW_CATEGORY) {
                    setCategoryMode("new");
                    return;
                  }
                  setCategoryMode("existing");
                  setField("courseCategory", value);
                }}
              >
                {!form.courseCategory && categoryMode !== "new" && (
                  <option value="">Select a category</option>
                )}
                {form.courseCategory === COMING_SOON && categoryMode !== "new" && (
                  <option value={COMING_SOON}>{COMING_SOON}</option>
                )}
                {categoryOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value={NEW_CATEGORY}>New category…</option>
              </select>
              {categoryMode === "new" && (
                <input
                  className="course-settings-input"
                  style={{ marginTop: 6 }}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="New category name"
                />
              )}

              {showReleaseFlags && (
                <>
                  <div
                    className="course-settings-checkbox"
                    onClick={() => setField("courseIsActive", !form.courseIsActive)}
                  >
                    <input
                      type="checkbox"
                      checked={form.courseIsActive}
                      onChange={(e) => setField("courseIsActive", e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    Active (visible to students)
                  </div>

                  <div
                    className="course-settings-checkbox"
                    onClick={() => setField("courseIsNew", !form.courseIsNew)}
                  >
                    <input
                      type="checkbox"
                      checked={form.courseIsNew}
                      onChange={(e) => setField("courseIsNew", e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    Show the &ldquo;New&rdquo; ribbon
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {course && (
          <div className="course-overview-footer">
            <span className="course-overview-count">
              {form.courseIsActive ? "Active" : "Inactive"}
              {form.courseCategory ? ` · ${form.courseCategory}` : ""}
            </span>
            <button
              className="bulk-btn bulk-btn-primary bulk-btn-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CourseSettingsPanel;
